/**
 * `<MapNav/>` — the editorial Map's secondary navigation: a two-tab
 * `Home · Taskmap` strip (#1043, IA shell, founder 2026-05-26).
 *
 * The IA was restructured from a three-bar stack (Harbour topbar + a duplicate
 * switcher-and-5-tabs content row + the editor toolbar) down to a clean two
 * levels: the shared Harbour topbar above (`Tasks · Team · Settings`, the
 * APP-level destinations) and this within-app SECONDARY nav, which only carries
 * the two surfaces that live INSIDE the tasks app:
 *
 *     Home · Taskmap
 *
 *   - **Home** (`/teams/:id/map`) — the overview dashboard (the DEFAULT landing
 *     since PR-D, founder F5 2026-05-26).
 *   - **Taskmap** (`/teams/:id/map/board`) — the canvas + outline workspace (the
 *     editor; the Outline folds INTO this window, it is no longer its own tab).
 *
 * Members and Settings moved UP to the Harbour topbar ("Team"/"Settings"), so
 * they are no longer tabs here. History is no longer a tab either (it becomes a
 * panel inside the Taskmap window in a later PR). The duplicate workspace
 * switcher this nav used to carry is gone — the topbar switcher is the only one.
 *
 * **Editorial-gated, legacy byte-identical.** This nav renders ONLY when the
 * per-team `task_editorial_ui` flag is on (`useMapTheme().mode === "editorial"`);
 * with the flag OFF it returns `null`, so the Map view bodies are byte-unchanged
 * and the shared Harbour topbar is the only nav (legacy shows no secondary nav).
 *
 * Tab idiom: left-aligned, comfortable hit targets + label size, an ink
 * active-underline, muted inactive — using the editorial theme palette + sans
 * font. Active resolution (PR-D): Home is active on the bare `/map` (and the
 * stale `/map/home` redirect); Taskmap is active on `/map/board` and any deeper
 * board sub-route.
 *
 * Isolation: taskmap-local + the editorial theme — no scheduler domain import,
 * and no shell cross-import (the switcher reference is REMOVED).
 */

import { Link, useLocation } from "react-router-dom";

import { useMapTheme } from "../theme/MapThemeContext";

/** Which of the two secondary-nav tabs is current. */
export type MapNavTab = "home" | "taskmap";

export interface MapNavProps {
  /** The team id — for the tab route hrefs. */
  teamId: string | undefined;
  /**
   * The active tab. The routed pages pass their own constant (`MapHomePage`
   * passes "taskmap", the Home dashboard passes "home"). When omitted, the
   * active tab is resolved from the current route (Home on `/map/home`,
   * Taskmap otherwise).
   */
  active?: MapNavTab;
}

interface TabSpec {
  key: MapNavTab;
  label: string;
  /** Route to navigate on click. */
  to: string;
}

/**
 * The editorial Map's `Home · Taskmap` secondary nav. Returns `null` in legacy
 * mode so the Map page bodies stay byte-identical (the gate).
 */
export function MapNav({ teamId, active }: MapNavProps) {
  const theme = useMapTheme();
  const location = useLocation();
  const c = theme.palette;

  // The gate: editorial-only. Legacy renders nothing (byte-identical bodies).
  if (theme.mode !== "editorial") return null;

  const base = teamId ? `/teams/${teamId}/map` : "#";
  const tabs: TabSpec[] = [
    { key: "home", label: "Home", to: base },
    { key: "taskmap", label: "Taskmap", to: teamId ? `${base}/board` : "#" },
  ];

  // Resolve the active tab. Prefer the explicit prop (the routed pages pass
  // their own); otherwise derive from the route — Taskmap on `/map/board`, Home
  // on the bare `/map` (and the `/map/home` redirect).
  const resolvedActive: MapNavTab = active ?? activeFromRoute(location.pathname);

  return (
    <div
      data-testid="map-nav"
      className="flex items-center border-b px-4"
      style={{
        borderColor: c.rule,
        backgroundColor: c.cardBg,
        fontFamily: theme.fonts.sans,
      }}
    >
      {/* Left-aligned, comfortable two-tab strip. Ink active underline; muted
          inactive. */}
      <nav data-testid="map-nav-tabs" className="flex items-center gap-1" aria-label="Map sections">
        {tabs.map((tab) => {
          const isActive = tab.key === resolvedActive;
          return (
            <Link
              key={tab.key}
              to={tab.to}
              data-testid={`map-nav-tab-${tab.key}`}
              aria-current={isActive ? "page" : undefined}
              className="whitespace-nowrap px-4 py-2.5 text-sm transition-colors"
              style={{
                color: isActive ? c.ink : c.muted,
                fontFamily: theme.fonts.sans,
                fontWeight: isActive ? 500 : 400,
                borderBottom: isActive ? `2px solid ${c.ink}` : "2px solid transparent",
              }}
            >
              {tab.label}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}

/**
 * Resolve the active tab from the URL when the caller doesn't pass one (PR-D).
 * Taskmap is active on `/map/board` (and any deeper board sub-route); Home is
 * active otherwise on the map routes (the bare `/map`, and the `/map/home`
 * stale-bookmark redirect target).
 */
function activeFromRoute(pathname: string): MapNavTab {
  if (/\/map\/board(\/|$)/.test(pathname)) return "taskmap";
  return "home";
}
